'use client'

import { useState, useRef, useEffect } from 'react'
import {
  Type, QrCode, Barcode, Image as ImageIcon, Minus, Square,
  Save, Eye, Trash2, Plus, Copy, RotateCw, Move, Maximize, Printer
} from 'lucide-react'
import TestPrintDialog from '../print/TestPrintDialog'

interface Zone {
  id: string
  zone_type: 'text' | 'qr' | 'barcode' | 'image' | 'line' | 'box'
  zone_name: string
  x: number
  y: number
  width: number
  height: number
  rotation: number
  content_rotation: number
  z_index: number
  field_mapping: string | null
  static_content: string | null
  font_family: string
  font_size: number
  font_weight: string
  font_style: string
  text_decoration: string
  text_align: string
  vertical_align: string
  text_color: string
  bg_color: string | null
  barcode_type: string
  qr_error_correction: string
  border_width: number
  border_color: string | null
  border_style: string | null
  padding: number
  visible: boolean
  locked: boolean
}

interface CanvasDesignerProps {
  templateId?: number
  onSave: (template: any) => void
  onCancel: () => void
}

export default function CanvasDesigner({ templateId, onSave, onCancel }: CanvasDesignerProps) {
  const [templateName, setTemplateName] = useState('New Template')
  const [templateDescription, setTemplateDescription] = useState('')
  const [canvasWidth, setCanvasWidth] = useState(4.0)
  const [canvasHeight, setCanvasHeight] = useState(6.0)
  const [canvasRotation, setCanvasRotation] = useState(0)
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait')
  const [scenario, setScenario] = useState<string | null>(null)
  const [isDefault, setIsDefault] = useState(false)
  const [zones, setZones] = useState<Zone[]>([])
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)
  const [activeTool, setActiveTool] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [showSampleData, setShowSampleData] = useState(false)
  const [showTestPrint, setShowTestPrint] = useState(false)
  const canvasRef = useRef<HTMLDivElement>(null)

  const DPI = 96 // pixels per inch

  // Load template data when templateId changes
  useEffect(() => {
    const loadTemplate = async () => {
      if (templateId) {
        try {
          const response = await fetch(`/api/templates-pg/${templateId}`)
          if (response.ok) {
            const result = await response.json()
            const template = result.data || result
            setTemplateName(template.name || 'New Template')
            setTemplateDescription(template.description || '')
            setCanvasWidth(Number(template.width) || 4.0)
            setCanvasHeight(Number(template.height) || 6.0)
            setCanvasRotation(Number(template.canvas_rotation) || 0)
            setOrientation(template.orientation || 'portrait')
            setScenario(template.scenario || null)
            setIsDefault(template.is_default || false)

            // Map zones with proper defaults
            const mappedZones = (template.zones || []).map((z: any, idx: number) => ({
              id: z.id || `zone-${idx}-${Date.now()}`,
              zone_type: z.zone_type || 'text',
              zone_name: z.zone_name || `${z.zone_type || 'Zone'} ${idx + 1}`,
              x: Number(z.x) || 0,
              y: Number(z.y) || 0,
              width: Number(z.width) || 1,
              height: Number(z.height) || 1,
              rotation: Number(z.rotation) || 0,
              content_rotation: Number(z.content_rotation) || 0,
              z_index: Number(z.z_index) || idx,
              field_mapping: z.field_mapping || null,
              static_content: z.static_content || null,
              font_family: z.font_family || 'Rajdhani',
              font_size: Number(z.font_size) || 12,
              font_weight: z.font_weight || 'normal',
              font_style: z.font_style || 'normal',
              text_decoration: z.text_decoration || 'none',
              text_align: z.text_align || 'left',
              vertical_align: z.vertical_align || 'middle',
              text_color: z.text_color || '#000000',
              bg_color: z.bg_color || null,
              barcode_type: z.barcode_type || 'code128',
              qr_error_correction: z.qr_error_correction || 'M',
              border_width: Number(z.border_width) || 0,
              border_color: z.border_color || null,
              border_style: z.border_style || null,
              padding: Number(z.padding) || 0,
              visible: z.visible !== false,
              locked: z.locked === true
            }))
            setZones(mappedZones)
          }
        } catch (error) {
          console.error('Error loading template:', error)
        }
      } else {
        // Reset to new template
        setTemplateName('New Template')
        setTemplateDescription('')
        setCanvasWidth(4.0)
        setCanvasHeight(6.0)
        setCanvasRotation(0)
        setOrientation('portrait')
        setScenario(null)
        setIsDefault(false)
        setZones([])
      }
    }

    loadTemplate()
  }, [templateId])

  const selectedZone = zones.find(z => z.id === selectedZoneId)

  // Sample data by scenario type
  const sampleDataByScenario: Record<string, Record<string, string>> = {
    'item': {
      'item_name': 'Nike Air Jordan Retro 1 High OG',
      'sku': 'NK-AJ1-BLK-10.5',
      'ic': '12345',
      'barcode': '12345',
      'category': 'Footwear',
      'brand': 'Nike',
      'model': 'Air Jordan 1 Retro High OG',
      'price': '$170.00',
      'cost': '$85.00',
      'size': '10.5',
      'color': 'Black/Red',
      'condition': 'New with Box',
      'status': 'Available',
      'quantity': '1',
      'weight': '1.5 lbs',
      'location': 'Shelf A-3'
    },
    'location': {
      'location_name': 'Warehouse A - Shelf 3B',
      'location_code': 'WH-A-3B',
      'zone': 'Zone A',
      'capacity': '500 units'
    },
    'shelf': {
      'shelf_name': 'Shelf 3B',
      'shelf_code': 'SH-3B',
      'location_name': 'Warehouse A',
      'tier_count': '4 tiers',
      'capacity': '100 units'
    },
    'tote': {
      'tote_name': 'Tote #42',
      'tote_code': 'TOTE-042',
      'location_name': 'Warehouse A - Shelf 3B',
      'capacity': '25 items'
    },
    'quantity_update': {
      'item_name': 'Nike Air Jordan Retro 1 High OG',
      'ic': '12345',
      'barcode': '12345',
      'old_quantity': '50',
      'new_quantity': '75',
      'change': '+25'
    },
    'generic': {}
  }

  // Get field options based on scenario
  const getFieldOptions = () => {
    try {
      const commonFields = [
        { value: '', label: 'Static Content (no mapping)' }
      ]

      if (!scenario || scenario === 'generic') {
        return commonFields
      }

      const scenarioData = sampleDataByScenario[scenario]
      if (!scenarioData) {
        return commonFields
      }

      const scenarioFields = Object.keys(scenarioData).map(key => ({
        value: key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      }))

      return [...commonFields, ...scenarioFields]
    } catch (error) {
      console.error('Error in getFieldOptions:', error)
      return [{ value: '', label: 'Static Content (no mapping)' }]
    }
  }

  const getZoneDisplayValue = (zone: Zone) => {
    // If sample data is enabled and there's a field mapping with data, use it
    if (showSampleData && zone.field_mapping && scenario && scenario !== 'generic') {
      const sampleValue = sampleDataByScenario[scenario]?.[zone.field_mapping]
      if (sampleValue) return sampleValue
    }

    // Use static_content if available
    if (zone.static_content) {
      return zone.static_content
    }

    // Show field mapping name as placeholder
    if (zone.field_mapping) {
      return `{${zone.field_mapping}}`
    }

    // Default fallback
    return zone.zone_name || 'Text'
  }

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!activeTool || !canvasRef.current) return

    const rect = canvasRef.current.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / DPI)
    const y = ((e.clientY - rect.top) / DPI)

    // Get default field mapping based on scenario
    const getDefaultFieldMapping = () => {
      if (!scenario || scenario === 'generic') return null

      const firstField = Object.keys(sampleDataByScenario[scenario] || {})[0]
      if (activeTool === 'text') return firstField || null
      if (activeTool === 'qr' || activeTool === 'barcode') {
        // Look for 'ic' or 'code' field first
        const fields = Object.keys(sampleDataByScenario[scenario] || {})
        return fields.find(f => f.includes('code') || f === 'ic') || fields[0] || null
      }
      return null
    }

    const newZone: Zone = {
      id: `zone-${Date.now()}`,
      zone_type: activeTool as any,
      zone_name: `${activeTool} Zone`,
      x: Math.max(0, x - 0.5),
      y: Math.max(0, y - 0.5),
      width: activeTool === 'qr' ? 1.0 : activeTool === 'barcode' ? 3.0 : 2.0,
      height: activeTool === 'qr' ? 1.0 : activeTool === 'barcode' ? 0.8 : activeTool === 'line' ? 0.05 : 0.5,
      rotation: 0,
      content_rotation: 0,
      z_index: zones.length,
      field_mapping: getDefaultFieldMapping(),
      static_content: null,
      font_family: 'Rajdhani',
      font_size: 12,
      font_weight: 'normal',
      font_style: 'normal',
      text_decoration: 'none',
      text_align: 'left',
      vertical_align: 'middle',
      text_color: '#000000',
      bg_color: null,
      barcode_type: 'code128',
      qr_error_correction: 'M',
      border_width: 0,
      border_color: null,
      border_style: null,
      padding: 0,
      visible: true,
      locked: false
    }

    setZones([...zones, newZone])
    setSelectedZoneId(newZone.id)
    setActiveTool(null)
  }

  const handleZoneMouseDown = (e: React.MouseEvent, zoneId: string) => {
    e.stopPropagation()
    setSelectedZoneId(zoneId)
    setIsDragging(true)
    setDragStart({ x: e.clientX, y: e.clientY })
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !selectedZoneId) return

    const dx = (e.clientX - dragStart.x) / DPI
    const dy = (e.clientY - dragStart.y) / DPI

    setZones(zones.map(z => {
      if (z.id === selectedZoneId) {
        return {
          ...z,
          x: Math.max(0, Math.min(canvasWidth - z.width, z.x + dx)),
          y: Math.max(0, Math.min(canvasHeight - z.height, z.y + dy))
        }
      }
      return z
    }))

    setDragStart({ x: e.clientX, y: e.clientY })
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    setIsResizing(false)
  }

  const handleDeleteZone = () => {
    if (!selectedZoneId) return
    setZones(zones.filter(z => z.id !== selectedZoneId))
    setSelectedZoneId(null)
  }

  const handleDuplicateZone = () => {
    if (!selectedZone) return
    const newZone: Zone = {
      ...selectedZone,
      id: `zone-${Date.now()}`,
      x: selectedZone.x + 0.2,
      y: selectedZone.y + 0.2,
      z_index: zones.length
    }
    setZones([...zones, newZone])
    setSelectedZoneId(newZone.id)
  }

  const updateZoneProperty = <K extends keyof Zone>(property: K, value: Zone[K]) => {
    if (!selectedZoneId) return
    setZones(zones.map(z => z.id === selectedZoneId ? { ...z, [property]: value } : z))
  }

  const handleSave = async () => {
    console.log('🔵 handleSave called - template name:', templateName)
    console.log('🔵 Template ID:', templateId)
    console.log('🔵 Zones count:', zones.length)

    // Auto-detect orientation based on dimensions
    const autoOrientation = canvasWidth > canvasHeight ? 'landscape' : 'portrait'
    console.log('🔵 Auto-detected orientation:', autoOrientation, '(width:', canvasWidth, 'height:', canvasHeight, ')')

    const templateData = {
      name: templateName,
      description: templateDescription,
      template_type: 'label',
      width: canvasWidth,
      height: canvasHeight,
      canvas_rotation: canvasRotation,
      orientation: autoOrientation,
      scenario,
      is_default: isDefault,
      active: true,
      zones: zones.map(z => ({
        zone_type: z.zone_type,
        zone_name: z.zone_name,
        x: z.x,
        y: z.y,
        width: z.width,
        height: z.height,
        rotation: z.rotation,
        content_rotation: z.content_rotation,
        z_index: z.z_index,
        field_mapping: z.field_mapping,
        static_content: z.static_content,
        font_family: z.font_family,
        font_size: z.font_size,
        font_weight: z.font_weight,
        font_style: z.font_style,
        text_decoration: z.text_decoration,
        text_align: z.text_align,
        vertical_align: z.vertical_align,
        text_color: z.text_color,
        bg_color: z.bg_color,
        barcode_type: z.barcode_type,
        qr_error_correction: z.qr_error_correction,
        border_width: z.border_width,
        border_color: z.border_color,
        border_style: z.border_style,
        padding: z.padding,
        visible: z.visible,
        locked: z.locked
      }))
    }

    try {
      const url = templateId ? `/api/templates-pg/${templateId}` : '/api/templates-pg'
      const method = templateId ? 'PUT' : 'POST'

      console.log('🔵 Saving to:', url, 'method:', method)
      console.log('🔵 Template data:', templateData)

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(templateData)
      })

      console.log('🔵 Response status:', response.status)
      const responseData = await response.json()
      console.log('🔵 Response data:', responseData)

      if (response.ok) {
        console.log('✅ Save successful, calling onSave callback')
        onSave(templateData)
      } else {
        console.error('❌ Save failed:', responseData)
        alert(`Failed to save template: ${responseData.error || 'Unknown error'}`)
      }
    } catch (error) {
      console.error('❌ Error saving template:', error)
      alert(`Error saving template: ${error}`)
    }
  }

  return (
    <>
      <style>{`
        /* Remove spinners from number inputs */
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type="number"] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
      `}</style>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '250px 1fr 300px',
        gap: '20px',
        minHeight: '600px',
        fontFamily: 'Rajdhani, sans-serif'
      }}>
      {/* Left Sidebar - Tools */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        borderRadius: '12px',
        padding: '20px',
        overflowY: 'auto'
      }}>
        <h3 style={{
          color: '#FF6B35',
          fontSize: '16px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '2px solid rgba(255, 107, 53, 0.3)'
        }}>
          📋 Template Info
        </h3>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
            TEMPLATE NAME
          </label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Enter template name..."
            style={{
              width: '100%',
              padding: '8px 12px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '14px'
            }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
            TEMPLATE TYPE
          </label>
          <select
            value={scenario || 'generic'}
            onChange={(e) => setScenario(e.target.value === 'generic' ? null : e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              borderRadius: '6px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <option value="generic">Generic (No Data)</option>
            <option value="item">Item Label</option>
            <option value="location">Location Label</option>
            <option value="shelf">Shelf Label</option>
            <option value="tote">Tote Label</option>
            <option value="quantity_update">Quantity Update</option>
          </select>
        </div>

        {scenario && scenario !== 'generic' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#FFD93D',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              padding: '12px',
              background: isDefault ? 'rgba(255, 217, 61, 0.1)' : 'rgba(0, 0, 0, 0.2)',
              border: isDefault ? '2px solid #FFD93D' : '1px solid rgba(148, 163, 184, 0.3)',
              borderRadius: '8px',
              transition: 'all 0.2s'
            }}>
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: '#FFD93D'
                }}
              />
              <span>⭐ Set as Default for {scenario.replace('_', ' ').toUpperCase()} labels</span>
            </label>
            {isDefault && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                background: 'rgba(255, 217, 61, 0.05)',
                border: '1px solid rgba(255, 217, 61, 0.2)',
                borderRadius: '6px',
                fontSize: '12px',
                color: '#8A95AA'
              }}>
                💡 This template will be automatically selected when printing {scenario.replace('_', ' ')} labels
              </div>
            )}
          </div>
        )}

        <h3 style={{
          color: '#FF6B35',
          fontSize: '16px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '16px',
          marginTop: '24px',
          paddingBottom: '12px',
          borderBottom: '2px solid rgba(255, 107, 53, 0.3)'
        }}>
          🛠 Zone Tools
        </h3>

        {[
          { id: 'text', icon: Type, label: 'Text Zone' },
          { id: 'qr', icon: QrCode, label: 'QR Code' },
          { id: 'barcode', icon: Barcode, label: 'Barcode' },
          { id: 'image', icon: ImageIcon, label: 'Image' },
          { id: 'line', icon: Minus, label: 'Line' },
          { id: 'box', icon: Square, label: 'Box/Shape' }
        ].map(tool => (
          <button
            key={tool.id}
            onClick={() => setActiveTool(activeTool === tool.id ? null : tool.id)}
            style={{
              width: '100%',
              padding: '12px',
              marginBottom: '8px',
              background: activeTool === tool.id ? 'rgba(255, 107, 53, 0.3)' : 'rgba(0, 0, 0, 0.3)',
              border: `1px solid ${activeTool === tool.id ? '#FF6B35' : 'rgba(148, 163, 184, 0.3)'}`,
              borderRadius: '6px',
              color: '#E0E6F0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              fontSize: '14px',
              fontWeight: 600,
              transition: 'all 0.2s',
              boxShadow: activeTool === tool.id ? '0 0 15px rgba(255, 107, 53, 0.3)' : 'none'
            }}
          >
            <tool.icon style={{ width: 24, height: 24 }} />
            <span>{tool.label}</span>
          </button>
        ))}

        {/* Layers */}
        <div style={{ marginTop: '24px' }}>
          <h3 style={{
            color: '#FF6B35',
            fontSize: '16px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '12px'
          }}>
            📚 Layers ({zones.length})
          </h3>

          {zones.map((zone, idx) => (
            <div
              key={zone.id}
              onClick={() => setSelectedZoneId(zone.id)}
              style={{
                background: selectedZoneId === zone.id ? 'rgba(0, 240, 255, 0.2)' : 'rgba(0, 0, 0, 0.3)',
                border: `1px solid ${selectedZoneId === zone.id ? '#00F0FF' : 'rgba(148, 163, 184, 0.2)'}`,
                borderRadius: '6px',
                padding: '8px 12px',
                marginBottom: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: selectedZoneId === zone.id ? '0 0 10px rgba(0, 240, 255, 0.3)' : 'none'
              }}
            >
              <div style={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>{zone.zone_name}</div>
              <div style={{ color: '#8A95AA', fontSize: '12px' }}>{zone.zone_type} - {zone.field_mapping || 'static'}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Center - Canvas */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Canvas Toolbar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingBottom: '16px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontSize: '18px', fontWeight: 700, color: '#00F0FF', textTransform: 'uppercase' }}>
              📐 Canvas - {canvasWidth.toFixed(1)}" × {canvasHeight.toFixed(1)}"
            </div>
            <button
              onClick={() => setShowSampleData(!showSampleData)}
              style={{
                padding: '6px 12px',
                background: showSampleData ? 'linear-gradient(135deg, #FFD93D, #FFA500)' : 'rgba(0, 0, 0, 0.3)',
                border: showSampleData ? '2px solid #FFD93D' : '1px solid rgba(148, 163, 184, 0.3)',
                borderRadius: '6px',
                color: showSampleData ? '#1A1F2E' : '#8A95AA',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '12px',
                textTransform: 'uppercase',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: showSampleData ? '0 0 15px rgba(255, 217, 61, 0.4)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <Eye style={{ width: 14, height: 14 }} />
              {showSampleData ? 'Sample Data ON' : 'Sample Data OFF'}
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="number"
              value={canvasWidth}
              onChange={(e) => setCanvasWidth(parseFloat(e.target.value) || 4.0)}
              step="0.1"
              min="1"
              max="12"
              style={{
                width: '60px',
                padding: '4px 8px',
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(148,163,184,0.3)',
                borderRadius: '4px',
                color: 'white',
                fontSize: '12px'
              }}
            />
            <span style={{ color: '#8A95AA' }}>×</span>
            <input
              type="number"
              value={canvasHeight}
              onChange={(e) => setCanvasHeight(parseFloat(e.target.value) || 6.0)}
              step="0.1"
              min="1"
              max="12"
              style={{
                width: '60px',
                padding: '4px 8px',
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(148,163,184,0.3)',
                borderRadius: '4px',
                color: 'white',
                fontSize: '12px'
              }}
            />
            <span style={{ color: '#8A95AA', marginLeft: '8px' }}>|</span>
            <span style={{ color: '#8A95AA', marginLeft: '8px', fontSize: '12px' }}>Rotation:</span>
            <input
              type="number"
              value={canvasRotation}
              onChange={(e) => setCanvasRotation(parseInt(e.target.value) || 0)}
              step="90"
              min="0"
              max="360"
              style={{
                width: '60px',
                padding: '4px 8px',
                background: 'rgba(0,0,0,0.3)',
                border: '1px solid rgba(148,163,184,0.3)',
                borderRadius: '4px',
                color: 'white',
                fontSize: '12px',
                marginLeft: '4px'
              }}
            />
            <span style={{ color: '#8A95AA', fontSize: '12px' }}>°</span>
          </div>
        </div>

        {/* Canvas Viewport */}
        <div style={{
          flex: 1,
          background: '#0F172A',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'auto'
        }}>
          <div
            ref={canvasRef}
            onClick={handleCanvasClick}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
              width: `${canvasWidth * DPI}px`,
              height: `${canvasHeight * DPI}px`,
              backgroundColor: '#FFFFFF',
              position: 'relative',
              boxShadow: '0 10px 50px rgba(0, 0, 0, 0.5)',
              cursor: activeTool ? 'crosshair' : 'default',
              transform: `rotate(${canvasRotation}deg)`,
              transformOrigin: 'center center'
            }}
          >
            {zones.map(zone => (
              <div
                key={zone.id}
                onMouseDown={(e) => handleZoneMouseDown(e, zone.id)}
                style={{
                  position: 'absolute',
                  left: `${zone.x * DPI}px`,
                  top: `${zone.y * DPI}px`,
                  width: `${zone.width * DPI}px`,
                  height: `${zone.height * DPI}px`,
                  border: selectedZoneId === zone.id ? '2px solid #00F0FF' : '1px dashed #94A3B8',
                  cursor: 'move',
                  boxShadow: selectedZoneId === zone.id ? '0 0 15px rgba(0, 240, 255, 0.5)' : 'none',
                  backgroundColor: zone.zone_type === 'qr' ? '#000000' :
                                 zone.zone_type === 'line' ? '#000000' :
                                 zone.zone_type === 'box' ? 'transparent' :
                                 zone.bg_color || '#F3F4F6',
                  display: 'flex',
                  alignItems: zone.vertical_align === 'top' ? 'flex-start' : zone.vertical_align === 'bottom' ? 'flex-end' : 'center',
                  justifyContent: zone.text_align === 'center' ? 'center' : zone.text_align === 'right' ? 'flex-end' : 'flex-start',
                  fontSize: `${zone.font_size}px`,
                  fontWeight: zone.font_weight,
                  fontFamily: zone.font_family,
                  color: zone.text_color,
                  padding: `${zone.padding}px`,
                  textAlign: zone.text_align as any,
                  overflow: 'hidden',
                  transform: `rotate(${zone.rotation}deg)`,
                  transformOrigin: 'center center'
                }}
              >
                {zone.zone_type === 'text' && (
                  <span style={{
                    fontSize: `${zone.font_size}px`,
                    fontWeight: zone.font_weight,
                    fontStyle: zone.font_style,
                    textDecoration: zone.text_decoration,
                    fontFamily: zone.font_family,
                    color: zone.text_color,
                    width: '100%',
                    textAlign: zone.text_align as any,
                    transform: `rotate(${zone.content_rotation}deg)`,
                    display: 'inline-block',
                    transformOrigin: 'center center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>
                    {getZoneDisplayValue(zone)}
                  </span>
                )}
                {zone.zone_type === 'qr' && (
                  <span style={{ color: 'white', fontSize: '8px', fontWeight: 'bold' }}>QR</span>
                )}
                {zone.zone_type === 'barcode' && (
                  <div style={{ display: 'flex', gap: '1px', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
                    {[...Array(Math.floor(zone.width * DPI / 3))].map((_, i) => (
                      <div key={i} style={{ width: '2px', height: '70%', background: '#000', flex: '0 0 2px' }} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Sidebar - Properties */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        borderRadius: '12px',
        padding: '20px',
        overflowY: 'auto'
      }}>
        <h3 style={{
          color: '#FF6B35',
          fontSize: '16px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          marginBottom: '16px',
          paddingBottom: '12px',
          borderBottom: '2px solid rgba(255, 107, 53, 0.3)'
        }}>
          ⚙ Properties
        </h3>

        {!selectedZone ? (
          <div style={{ color: '#8A95AA', textAlign: 'center', padding: '40px 20px' }}>
            Select a zone to edit properties
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                ZONE NAME
              </label>
              <input
                type="text"
                value={selectedZone.zone_name}
                onChange={(e) => updateZoneProperty('zone_name', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px'
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                FIELD MAPPING
              </label>
              <select
                value={selectedZone.field_mapping || ''}
                onChange={(e) => updateZoneProperty('field_mapping', e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px'
                }}
              >
                {getFieldOptions().map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                STATIC CONTENT
              </label>
              <input
                type="text"
                value={selectedZone.static_content || ''}
                onChange={(e) => updateZoneProperty('static_content', e.target.value || null)}
                placeholder="Enter static text or fallback value"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  borderRadius: '6px',
                  color: 'white',
                  fontSize: '14px'
                }}
              />
              <div style={{ color: '#6B7280', fontSize: '11px', marginTop: '4px' }}>
                Used when field mapping is empty or as fallback
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                  WIDTH (in)
                </label>
                <input
                  type="number"
                  value={selectedZone.width}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value)
                    updateZoneProperty('width', isNaN(val) ? 0 : val)
                  }}
                  step="0.1"
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                  HEIGHT (in)
                </label>
                <input
                  type="number"
                  value={selectedZone.height}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value)
                    updateZoneProperty('height', isNaN(val) ? 0 : val)
                  }}
                  step="0.1"
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* Rotation Controls */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <div>
                <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                  ELEMENT ROTATION (°)
                </label>
                <input
                  type="number"
                  value={selectedZone.rotation}
                  onChange={(e) => updateZoneProperty('rotation', parseInt(e.target.value) || 0)}
                  min="0"
                  max="360"
                  step="5"
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                  CONTENT ROTATION (°)
                </label>
                <input
                  type="number"
                  value={selectedZone.content_rotation}
                  onChange={(e) => updateZoneProperty('content_rotation', parseInt(e.target.value) || 0)}
                  min="0"
                  max="360"
                  step="5"
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(0, 0, 0, 0.3)',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {selectedZone.zone_type === 'text' && (
              <>
                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                    FONT FAMILY
                  </label>
                  <select
                    value={selectedZone.font_family}
                    onChange={(e) => updateZoneProperty('font_family', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  >
                    <option value="Rajdhani">Rajdhani</option>
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Times New Roman">Times New Roman</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Verdana">Verdana</option>
                    <option value="Impact">Impact</option>
                    <option value="Comic Sans MS">Comic Sans MS</option>
                    <option value="Trebuchet MS">Trebuchet MS</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                    FONT SIZE (pt)
                  </label>
                  <input
                    type="number"
                    value={selectedZone.font_size}
                    onChange={(e) => {
                      const val = parseInt(e.target.value)
                      updateZoneProperty('font_size', isNaN(val) ? 0 : val)
                    }}
                    min="6"
                    max="72"
                    style={{
                      width: '100%',
                      padding: '8px',
                      background: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(148, 163, 184, 0.3)',
                      borderRadius: '6px',
                      color: 'white',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                    TEXT ALIGN
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => updateZoneProperty('text_align', 'left')}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: selectedZone.text_align === 'left' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.text_align === 'left' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.text_align === 'left' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}
                    >
                      ⬅
                    </button>
                    <button
                      onClick={() => updateZoneProperty('text_align', 'center')}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: selectedZone.text_align === 'center' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.text_align === 'center' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.text_align === 'center' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}
                    >
                      ⬌
                    </button>
                    <button
                      onClick={() => updateZoneProperty('text_align', 'right')}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: selectedZone.text_align === 'right' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.text_align === 'right' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.text_align === 'right' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}
                    >
                      ➡
                    </button>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                    VERTICAL ALIGN
                  </label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => updateZoneProperty('vertical_align', 'top')}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: selectedZone.vertical_align === 'top' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.vertical_align === 'top' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.vertical_align === 'top' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}
                    >
                      ⬆
                    </button>
                    <button
                      onClick={() => updateZoneProperty('vertical_align', 'middle')}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: selectedZone.vertical_align === 'middle' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.vertical_align === 'middle' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.vertical_align === 'middle' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}
                    >
                      ⬍
                    </button>
                    <button
                      onClick={() => updateZoneProperty('vertical_align', 'bottom')}
                      style={{
                        flex: 1,
                        padding: '8px',
                        background: selectedZone.vertical_align === 'bottom' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.vertical_align === 'bottom' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.vertical_align === 'bottom' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}
                    >
                      ⬇
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                      BOLD
                    </label>
                    <button
                      onClick={() => updateZoneProperty('font_weight', selectedZone.font_weight === 'bold' ? 'normal' : 'bold')}
                      style={{
                        width: '100%',
                        padding: '8px',
                        background: selectedZone.font_weight === 'bold' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.font_weight === 'bold' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.font_weight === 'bold' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '16px'
                      }}
                    >
                      B
                    </button>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                      ITALIC
                    </label>
                    <button
                      onClick={() => updateZoneProperty('font_style', selectedZone.font_style === 'italic' ? 'normal' : 'italic')}
                      style={{
                        width: '100%',
                        padding: '8px',
                        background: selectedZone.font_style === 'italic' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.font_style === 'italic' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.font_style === 'italic' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        fontStyle: 'italic',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}
                    >
                      I
                    </button>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#8A95AA', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>
                      UNDERLINE
                    </label>
                    <button
                      onClick={() => updateZoneProperty('text_decoration', selectedZone.text_decoration === 'underline' ? 'none' : 'underline')}
                      style={{
                        width: '100%',
                        padding: '8px',
                        background: selectedZone.text_decoration === 'underline' ? 'rgba(0, 240, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid ${selectedZone.text_decoration === 'underline' ? '#00F0FF' : 'rgba(148, 163, 184, 0.3)'}`,
                        borderRadius: '6px',
                        color: selectedZone.text_decoration === 'underline' ? '#00F0FF' : '#8A95AA',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        fontWeight: 600,
                        fontSize: '16px'
                      }}
                    >
                      U
                    </button>
                  </div>
                </div>
              </>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button
                onClick={handleDuplicateZone}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(0, 240, 255, 0.2)',
                  border: '1px solid #00F0FF',
                  borderRadius: '6px',
                  color: '#00F0FF',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Copy style={{ width: 16, height: 16 }} />
                Duplicate
              </button>
              <button
                onClick={handleDeleteZone}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: 'rgba(255, 0, 0, 0.2)',
                  border: '1px solid #FF0000',
                  borderRadius: '6px',
                  color: '#FF6B6B',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                <Trash2 style={{ width: 16, height: 16 }} />
                Delete
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(148, 163, 184, 0.2)' }}>
          <button
            onClick={() => setShowTestPrint(true)}
            style={{
              width: '100%',
              padding: '12px',
              background: '#242938',
              border: '2px solid rgba(255, 217, 61, 0.5)',
              borderRadius: '6px',
              color: '#FFD93D',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}
          >
            <Printer style={{ width: 16, height: 16 }} />
            Test Print
          </button>
          <button
            onClick={handleSave}
            style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #FFD93D, #FFA500)',
              border: '2px solid #FFD93D',
              borderRadius: '6px',
              color: '#1A1F2E',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: '14px',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 0 20px rgba(255, 217, 61, 0.4)',
              marginBottom: '8px'
            }}
          >
            <Save style={{ width: 16, height: 16 }} />
            Save Template
          </button>
          <button
            onClick={onCancel}
            style={{
              width: '100%',
              padding: '12px',
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              borderRadius: '6px',
              color: '#8A95AA',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
        </div>
      </div>

      <TestPrintDialog
        isOpen={showTestPrint}
        onClose={() => setShowTestPrint(false)}
        template={{
          name: templateName,
          width: canvasWidth,
          height: canvasHeight,
          scenario: scenario,
          zones: zones
        }}
      />
    </div>
    </>
  )
}
